// src/pages/CapturePage/blocks/MouthGymBlock.js
//
// Mouth Gym — structures at rest and held postures, on a split screen: the
// child sees the camera and a shape to copy, the examiner ticks a checklist
// beside it.
//
// No video is stored. Frames are processed on the device by the same FaceMesh
// the Oral Motor exercises use, and only the numbers — lip ratio, jaw opening,
// symmetry — reach the server, beside the examiner's own observations. The
// camera permission therefore governs *using* the camera, not keeping footage:
// the block is closed when a family declined it, with the reason said plainly.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useFaceMesh from '../../OralMotorPage/useFaceMesh';
import {
    canScoreShape, computeMatchScore, computeMouthMetrics, LIP_INNER_LOOP, LIP_OUTER_LOOP,
} from '../../OralMotorPage/shapeDetector';
import { DETECTOR_VERSION, MODEL_VERSION } from '../../OralMotorPage/useFaceMesh';
import { captureError, saveOralMotor } from '../captureApi';

const STRUCTURE_CHOICES = [
    { key: 'normal', label: 'Normal' },
    { key: 'atypical', label: 'Atypical' },
    { key: 'not_seen', label: 'Not observed' },
];
// A guide, not a verdict — see the drawing effect below for why it never changes.
const LIP_GUIDE = '#7cc4ff';
const POSTURE_CHOICES = [
    { key: 'achieved', label: 'Achieved' },
    { key: 'partial', label: 'Partial' },
    { key: 'not_achieved', label: 'Not achieved' },
    { key: 'not_attempted', label: 'Not attempted' },
];

export default function MouthGymBlock({ session, block, onSaved, onFinish }) {
    const existing = session.oral_motor?.observations || {};
    const [observations, setObservations] = useState(existing);
    const [notes, setNotes] = useState(session.oral_motor?.notes || '');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [cameraOn, setCameraOn] = useState(false);
    const [postureIndex, setPostureIndex] = useState(0);

    const videoRef = useRef(null);
    const lipCanvasRef = useRef(null);
    const streamRef = useRef(null);
    const measuresRef = useRef({});
    const { landmarks, blendshapes, isLoaded } = useFaceMesh(videoRef, cameraOn);

    const postures = block.postures || [];
    const structures = block.structures || [];
    const posture = postures[postureIndex];

    useEffect(() => {
        if (!cameraOn) return undefined;
        // `mediaDevices` is absent, not empty, on an insecure origin — a session
        // opened over plain http threw here and took the whole block down with it,
        // checklist included. The checklist is the clinical record; it has to
        // survive the camera.
        if (!navigator.mediaDevices?.getUserMedia) {
            setError('This browser will not give the page a camera — a secure (https) '
                + 'connection is needed. The checklist can still be filled in.');
            return undefined;
        }
        let cancelled = false;
        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
            .then((stream) => {
                if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(() => { /* autoplay policy; the tap already happened */ });
                }
            })
            .catch(() => setError('The camera did not open. The checklist can still be filled in.'));
        return () => {
            cancelled = true;
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }
        };
    }, [cameraOn]);

    const metrics = useMemo(
        () => (landmarks ? computeMouthMetrics(landmarks, blendshapes) : null),
        [landmarks, blendshapes]
    );

    // The child's own mouth, outlined on the camera, so a four-year-old can see
    // what the screen is looking at.
    //
    // One colour, always. In the Oral Motor practice exercises this outline turns
    // green as the score rises, and that is right there — it is a game. Here it
    // must not: the match figure is deliberately withheld until the examiner has
    // written their own judgement down, and an outline that went green would hand
    // it to them anyway, through the corner of their eye.
    useEffect(() => {
        const canvas = lipCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;                       // no 2d context under jsdom

        // Normalised landmarks (0–1) map linearly onto the video's own pixels, so
        // the backing store follows the video rather than the CSS box.
        const video = videoRef.current;
        const w = video?.videoWidth || 320;
        const h = video?.videoHeight || 240;
        if (canvas.width !== w) canvas.width = w;
        if (canvas.height !== h) canvas.height = h;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!landmarks) return;

        const drawLoop = (indices, lineWidth) => {
            ctx.beginPath();
            indices.forEach((index, i) => {
                const point = landmarks[index];
                if (!point) return;
                const x = point.x * canvas.width;
                const y = point.y * canvas.height;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            });
            ctx.closePath();
            ctx.lineWidth = lineWidth;
            ctx.strokeStyle = LIP_GUIDE;
            ctx.lineJoin = 'round';
            ctx.stroke();
        };

        ctx.shadowColor = LIP_GUIDE;
        ctx.shadowBlur = 8;
        drawLoop(LIP_OUTER_LOOP, 2);
        ctx.shadowBlur = 0;
        drawLoop(LIP_INNER_LOOP, 1.5);
    }, [landmarks]);
    // Three of the postures here are tongue movements no camera-based detector
    // can judge — Face Mesh has no tongue landmarks. Their meter sat at zero,
    // which reads as "the child failed" rather than "this was never measured".
    const scorable = posture ? canScoreShape(posture.shape, metrics) : false;
    const score = posture && metrics && scorable ? computeMatchScore(posture.shape, metrics) : 0;
    // Judged = this posture already has the examiner's observation recorded.
    const judged = Boolean(posture && observations[posture.key]);

    // Keep the best reading of each posture. A single frame catches a blink;
    // the best held frame is what the examiner actually saw.
    useEffect(() => {
        if (!posture || !metrics) return;
        // The mouth is genuinely measured whatever the posture, so these are kept
        // for all of them. `match` is not: for an unscorable shape there is no
        // score, and storing a zero would record a measurement nobody took.
        const reading = {
            open_ratio: metrics.openRatio != null ? Math.round(metrics.openRatio * 1000) / 1000 : null,
            mouth_width: metrics.normalizedMouthWidth != null
                ? Math.round(metrics.normalizedMouthWidth * 1000) / 1000 : null,
            jaw_open: metrics.jawOpen,
            smile_left: metrics.smileLeft,
            smile_right: metrics.smileRight,
        };
        if (!scorable) {
            measuresRef.current[posture.key] = reading;
            return;
        }
        const prev = measuresRef.current[posture.key];
        if (!prev || prev.camera_cue == null || score > prev.camera_cue) {
            measuresRef.current[posture.key] = {
                // Named for what it is. It was `match`, which beside real
                // measurements read as one: it is a blendshape score over a
                // threshold nobody validated clinically — no unit, no norm, not
                // comparable between postures or between sittings. The bar the
                // child sees is drawn from it, and that is its whole job.
                camera_cue: Math.round(score * 100) / 100,
                ...reading,
            };
        }
    }, [metrics, posture, score, scorable]);

    const setObservation = (key, value) =>
        setObservations((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        setError('');
        setSaving(true);
        try {
            const r = await saveOralMotor(session.id, {
                observations,
                // The detector travels under a reserved key — nothing iterating
                // the measures can mistake it for a posture. Without it, two
                // sittings of the same child can be compared without anyone
                // knowing whether the same detector produced both.
                measures: {
                    ...measuresRef.current,
                    _detector: { library: DETECTOR_VERSION, model: MODEL_VERSION },
                },
                notes: notes.trim() || null,
            });
            onSaved(r.data);
            onFinish();
        } catch (e) {
            setError(captureError(e, 'Could not save the examination.'));
        } finally {
            setSaving(false);
        }
    };

    if (!session.video_allowed) {
        return (
            <div className="cap-card">
                <h3>{block.clinical_name?.en}</h3>
                <p className="cap-note red">
                    {session.consent_reason
                        || 'The guardian did not give permission for the camera, so this block is closed.'}
                </p>
                <p className="cap-hint">
                    Nothing is filmed in this block even when it runs — only measurements are kept —
                    but the camera itself needs permission.
                </p>
                <button type="button" className="cap-btn ghost" onClick={onFinish}>Back to the session</button>
            </div>
        );
    }

    return (
        <div className="cap-split">
            <div className="cap-split-child">
              <div className="cap-split-child-inner">
                <h2>{block.child_title?.hi}</h2>
                <p className="cap-instruction">{block.instruction?.hi} <em>{block.instruction?.en}</em></p>

                {cameraOn ? (
                    <div className="cap-video-wrap">
                        <video ref={videoRef} className="cap-video" playsInline muted />
                        <canvas ref={lipCanvasRef} className="cap-lip-canvas" />
                        {!isLoaded ? <p className="cap-hint">Getting the camera ready…</p> : null}
                    </div>
                ) : (
                    <button type="button" className="cap-btn big" onClick={() => setCameraOn(true)}>
                        Turn on the camera
                    </button>
                )}

                {posture ? (
                    <div className="cap-posture">
                        <span className="cap-posture-name">{posture.hi}</span>
                        <span className="cap-posture-en">{posture.en}</span>
                        {/* The device's reading is hidden until the examiner has
                            recorded their own judgement for this posture. The
                            therapist asked for this: a number on screen pulls an
                            observation toward it, and the observation is the
                            clinical finding. It is still being measured — just
                            not shown yet. */}
                        {!scorable ? (
                            <span className="cap-posture-note">
                                The camera cannot judge this one — your judgement is what gets
                                recorded.
                            </span>
                        ) : judged ? (
                            <div className="cap-progress">
                                <span style={{ width: `${Math.round(score * 100)}%` }} />
                            </div>
                        ) : (
                            <span className="cap-posture-hold">
                                {block.hold_seconds ? `Hold for ${block.hold_seconds}` : 'Hold it'}
                            </span>
                        )}
                    </div>
                ) : null}

                <p className="cap-hint">No video is recorded — only the measurements.</p>
              </div>
            </div>

            <div className="cap-split-examiner">
                <h3>Structures at rest</h3>
                {structures.map((item) => (
                    <div key={item.key} className="cap-obs">
                        <div className="cap-obs-label">
                            <strong>{item.en}</strong> <span>{item.hi}</span>
                            {item.expected?.en ? <em>expected: {item.expected.en}</em> : null}
                        </div>
                        <div className="cap-choices small">
                            {STRUCTURE_CHOICES.map((c) => (
                                <button
                                    key={c.key} type="button"
                                    className={`cap-choice${observations[item.key] === c.key ? ' selected' : ''}`}
                                    onClick={() => setObservation(item.key, c.key)}
                                >{c.label}</button>
                            ))}
                        </div>
                    </div>
                ))}

                <h3>Held postures</h3>
                {postures.map((item, i) => (
                    <div key={item.key} className={`cap-obs${i === postureIndex ? ' current' : ''}`}>
                        <div className="cap-obs-label">
                            <button type="button" className="cap-linkish" onClick={() => setPostureIndex(i)}>
                                <strong>{item.en}</strong> <span>{item.hi}</span>
                            </button>
                        </div>
                        <div className="cap-choices small">
                            {POSTURE_CHOICES.map((c) => (
                                <button
                                    key={c.key} type="button"
                                    className={`cap-choice${observations[item.key] === c.key ? ' selected' : ''}`}
                                    onClick={() => { setObservation(item.key, c.key); setPostureIndex(i); }}
                                >{c.label}</button>
                            ))}
                        </div>
                        {/* Revealed after the judgement, never before it. */}
                        {observations[item.key] && measuresRef.current[item.key] ? (
                            <div className="cap-measured-after">
                                {measuresRef.current[item.key].camera_cue != null
                                    ? `camera cue ${measuresRef.current[item.key].camera_cue} — not a measurement`
                                    : 'your judgement only — the camera cannot score this posture'}
                                {measuresRef.current[item.key].open_ratio != null
                                    ? ` · open ${measuresRef.current[item.key].open_ratio}` : ''}
                            </div>
                        ) : null}
                    </div>
                ))}

                <label className="cap-field">
                    <span>Notes</span>
                    <textarea rows="3" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </label>

                {error ? <p className="cap-error">{error}</p> : null}
                <div className="cap-actions">
                    <button type="button" className="cap-btn" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save examination'}
                    </button>
                    <button type="button" className="cap-btn ghost" onClick={onFinish}>Back</button>
                </div>
            </div>
        </div>
    );
}
